'use client';

import { useEffect, useRef, useSyncExternalStore } from 'react';
import { clearGlobalToast, getGlobalToastSnapshot, subscribeGlobalToast } from '@/lib/globalToast';

const TOAST_DURATION_MS = 8000;

export function GlobalToastHost() {
  const toast = useSyncExternalStore(subscribeGlobalToast, getGlobalToastSnapshot, getGlobalToastSnapshot);
  const dismissTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!toast) return;
    if (dismissTimer.current) {
      clearTimeout(dismissTimer.current);
    }
    dismissTimer.current = setTimeout(() => clearGlobalToast(toast.id), TOAST_DURATION_MS);

    return () => {
      if (dismissTimer.current) {
        clearTimeout(dismissTimer.current);
      }
    };
  }, [toast]);

  if (!toast) return null;

  const isInfo = toast.variant === 'info';
  const accent = isInfo ? 'var(--accent-primary)' : 'var(--error-text)';

  return (
    <div
      role={isInfo ? 'status' : 'alert'}
      aria-live={isInfo ? 'polite' : 'assertive'}
      style={{
        position: 'fixed',
        top: '80px',
        right: '24px',
        zIndex: 100001,
        maxWidth: '420px',
        display: 'flex',
        gap: '12px',
        alignItems: 'flex-start',
        padding: '14px 18px',
        borderRadius: '8px',
        border: `1px solid ${accent}`,
        background: 'var(--bg-primary)',
        boxShadow: '0 8px 24px var(--shadow-md)',
        color: 'var(--text-primary)',
      }}
    >
      <div
        aria-hidden="true"
        style={{
          width: '22px',
          height: '22px',
          borderRadius: '50%',
          background: accent,
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          fontSize: 'var(--text-sm)',
          fontWeight: 700,
          lineHeight: 1,
        }}
      >
        {isInfo ? 'i' : '!'}
      </div>
      <div>
        <div style={{ fontSize: 'var(--text-sm)', fontWeight: 700, marginBottom: toast.message ? '4px' : 0 }}>
          {toast.title}
        </div>
        {toast.message && (
          <div style={{ fontSize: 'var(--text-control)', lineHeight: 1.4 }}>
            {toast.message}
          </div>
        )}
        {toast.action && (
          <button
            type="button"
            onClick={() => { toast.action!.onClick(); clearGlobalToast(toast.id); }}
            style={{
              marginTop: '8px', padding: '3px 10px', borderRadius: '6px', cursor: 'pointer',
              border: `1px solid ${accent}`, background: 'transparent', color: accent,
              fontFamily: 'inherit', fontSize: 'var(--text-xs)', fontWeight: 600,
            }}
          >
            {toast.action.label}
          </button>
        )}
      </div>
    </div>
  );
}
